// CShopScene — 商店：好運月卡與三種金幣包；每一項主按鈕是「購買」，下方小字可「請人幫忙付」
// 對應 docs/PRD-Freemium.md 第 5、6 節、docs/Shop-Help-Design.md
//
// 畫面上以自己購買為主，請人代付是附加選項；文案不提「孩子」。
// 付款（Stripe）尚未開通：「購買」先提示即將開放；「請人幫忙付」產生代買連結，用 LINE 傳給親友，對方在代買頁付款。
//
// === 使用圖片 ===
// - public/assets/ui/shop-card.png（key: SHOP_CARD_KEY）— 好運月卡（Codex 產圖）
// - public/assets/ui/shop-pack-s.png／shop-pack-m.png／shop-pack-l.png（SHOP_PACK_*_KEY）— 金幣包（Codex 產圖）
// - 桌面：createTableBackground（Phaser.Graphics）；面板與按鈕：page-skin／dialog-skin，詳見各檔頭
// - 右上 X：close-button（純 Graphics）；輔 15 標示為純程式繪製

import Phaser from 'phaser';
import { BGMTrack } from '@audio/audio-keys';
import { playUIClick, syncBackgroundMusic } from '@audio/scene-audio';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '@config/game-config';
import { requestHelp } from '@net/account-client';
import { SHOP_ITEMS, ShopItemId, type IShopItem, type TShopItemId } from '@net/shop-catalog';
import { createCloseButton, CloseButtonLayout } from '@ui/close-button';
import { createDialogButtonSurface } from '@ui/dialog-skin';
import { noticeFromError, showNotice } from '@ui/notice-toast';
import { createPagePanel, createPageTitle, PAGE_THEME } from '@ui/page-skin';
import { createTableBackground } from '@ui/table-background';
import { SceneKey } from './scene-keys';

export const SHOP_CARD_KEY = 'shop-card';
export const SHOP_PACK_S_KEY = 'shop-pack-s';
export const SHOP_PACK_M_KEY = 'shop-pack-m';
export const SHOP_PACK_L_KEY = 'shop-pack-l';

const ITEM_IMAGE_KEY: Record<TShopItemId, string> = {
  [ShopItemId.MonthlyCard]: SHOP_CARD_KEY,
  [ShopItemId.PackSmall]: SHOP_PACK_S_KEY,
  [ShopItemId.PackMedium]: SHOP_PACK_M_KEY,
  [ShopItemId.PackLarge]: SHOP_PACK_L_KEY,
};

// ---- 版面常數 ----
const PANEL_X = LOGICAL_WIDTH / 2;
const PANEL_Y = LOGICAL_HEIGHT / 2;
const PANEL_WIDTH = 1180;
const PANEL_HEIGHT = 660;
const TITLE_Y = 62;
const SUBTITLE_Y = 108;
const SUBTITLE_FONT = 22;
const CARD_X = 340; // 月卡區中心
const CARD_Y = 400;
const CARD_WIDTH = 460;
const CARD_HEIGHT = 470;
const CARD_IMAGE_Y = CARD_Y - 152;
const CARD_IMAGE_WIDTH = 210;
const CARD_NAME_Y = CARD_Y - 36;
const CARD_PRICE_Y = CARD_Y + 6;
const CARD_HIGHLIGHT_Y = CARD_Y + 46;
const CARD_HIGHLIGHT_STEP = 30;
const CARD_BUTTON_Y = CARD_Y + 160; // 「購買」主按鈕
const CARD_HELP_Y = CARD_Y + 210; // 「請人幫忙付」小字
const PACK_X = 880; // 金幣包區中心
const PACK_START_Y = 230;
const PACK_STEP_Y = 150;
const PACK_WIDTH = 560;
const PACK_HEIGHT = 134;
const PACK_IMAGE_X = PACK_X - 220;
const PACK_IMAGE_SIZE = 108;
const PACK_TEXT_X = PACK_X - 150;
const PACK_BUTTON_X = PACK_X + 180;
const PACK_BUTTON_OFFSET_Y = -20; // 「購買」主按鈕相對金幣包中心
const PACK_HELP_OFFSET_Y = 38; // 「請人幫忙付」小字相對金幣包中心
const BUTTON_WIDTH = 180;
const BUTTON_HEIGHT = 56;
const BUTTON_FONT = 28;
const HELP_LINK_FONT = 20;
const HELP_LINK_COLOR = '#d8e8dc';
const HELP_LINK_LINE = 0xd8e8dc; // 小字底線
const HELP_LINK_LINE_HOVER = 0xffffff;
const HELP_LINK_PAD = 10; // 小字點擊範圍往外加大（長輩手指）
const NAME_FONT = 32;
const PRICE_FONT = 28;
const TEXT_FONT = 22;
const BOX_FILL = 0x12503a;
const BOX_STROKE = 0xe7c26b;
const BOX_RADIUS = 18;
const LEGAL_Y = 664; // 警語（面板內最下方）
const LEGAL_FONT = 17;
const RATING_X = 120; // 輔 15 標示中心
const RATING_Y = 92;
const RATING_SIZE = 56; // 法規要求至少 45×45 px
const RATING_COLOR = 0xd35400;

export class CShopScene extends Phaser.Scene {
  private busy = false;

  constructor() {
    super({ key: SceneKey.Shop });
  }

  create(): void {
    this.busy = false;
    syncBackgroundMusic(this, BGMTrack.Lobby);
    createTableBackground(this);
    createPagePanel(this, PANEL_X, PANEL_Y, PANEL_WIDTH, PANEL_HEIGHT);
    createPageTitle(this, PANEL_X, TITLE_Y, '商店');
    this.add
      .text(PANEL_X, SUBTITLE_Y, '買月卡最划算；也可以按「請人幫忙付」，用 LINE 傳給親友代付', {
        color: PAGE_THEME.muted,
        fontFamily: PAGE_THEME.font,
        fontSize: `${SUBTITLE_FONT}px`,
      })
      .setOrigin(0.5);
    this.renderRating();
    const [card, ...packs] = SHOP_ITEMS;
    if (card) this.renderCard(card);
    packs.forEach((item, index) => this.renderPack(item, PACK_START_Y + index * PACK_STEP_Y));
    this.add
      .text(
        PANEL_X,
        LEGAL_Y,
        '本遊戲為輔導十五歲級・不得利用於賭博・金幣為遊戲內虛擬道具，不可兌換現金或轉讓',
        { color: PAGE_THEME.muted, fontFamily: PAGE_THEME.font, fontSize: `${LEGAL_FONT}px` }
      )
      .setOrigin(0.5);
    createCloseButton(
      this,
      PANEL_X + PANEL_WIDTH / 2 - CloseButtonLayout.insetX,
      PANEL_Y - PANEL_HEIGHT / 2 + CloseButtonLayout.insetY,
      () => this.goBack()
    );
    this.input.keyboard?.on('keydown-ESC', () => this.goBack());
  }

  private renderRating(): void {
    const g = this.add.graphics();
    g.lineStyle(4, RATING_COLOR);
    g.strokeRoundedRect(
      RATING_X - RATING_SIZE / 2,
      RATING_Y - RATING_SIZE / 2,
      RATING_SIZE,
      RATING_SIZE,
      8
    );
    this.add
      .text(RATING_X, RATING_Y, '輔15', {
        color: '#ff8a3d',
        fontFamily: PAGE_THEME.font,
        fontSize: '20px',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  private renderBox(x: number, y: number, width: number, height: number): void {
    const g = this.add.graphics();
    g.fillStyle(BOX_FILL, 1);
    g.fillRoundedRect(x - width / 2, y - height / 2, width, height, BOX_RADIUS);
    g.lineStyle(3, BOX_STROKE);
    g.strokeRoundedRect(x - width / 2, y - height / 2, width, height, BOX_RADIUS);
  }

  private renderCard(item: IShopItem): void {
    this.renderBox(CARD_X, CARD_Y, CARD_WIDTH, CARD_HEIGHT);
    const image = this.add.image(CARD_X, CARD_IMAGE_Y, ITEM_IMAGE_KEY[item.id]);
    image.setScale(CARD_IMAGE_WIDTH / image.width);
    this.text(CARD_X, CARD_NAME_Y, item.name, NAME_FONT, '#ffe9a8', true);
    this.text(CARD_X, CARD_PRICE_Y, `NT$${item.priceTwd}／月`, PRICE_FONT, '#ffffff', true);
    item.highlights.forEach((line, i) =>
      this.text(CARD_X, CARD_HIGHLIGHT_Y + i * CARD_HIGHLIGHT_STEP, `・${line}`, TEXT_FONT)
    );
    this.renderBuyButton(CARD_X, CARD_BUTTON_Y);
    this.renderHelpLink(CARD_X, CARD_HELP_Y, item);
  }

  private renderPack(item: IShopItem, y: number): void {
    this.renderBox(PACK_X, y, PACK_WIDTH, PACK_HEIGHT);
    const image = this.add.image(PACK_IMAGE_X, y, ITEM_IMAGE_KEY[item.id]);
    image.setScale(PACK_IMAGE_SIZE / Math.max(image.width, image.height));
    this.text(PACK_TEXT_X, y - 30, item.name, NAME_FONT - 4, '#ffe9a8', true, 0);
    this.text(
      PACK_TEXT_X,
      y + 8,
      `${item.coins.toLocaleString()} 金幣`,
      TEXT_FONT,
      '#ffffff',
      false,
      0
    );
    this.text(
      PACK_TEXT_X,
      y + 40,
      `NT$${item.priceTwd}・${item.highlights.join('・')}`,
      TEXT_FONT - 2,
      PAGE_THEME.muted,
      false,
      0
    );
    this.renderBuyButton(PACK_BUTTON_X, y + PACK_BUTTON_OFFSET_Y);
    this.renderHelpLink(PACK_BUTTON_X, y + PACK_HELP_OFFSET_Y, item);
  }

  private text(
    x: number,
    y: number,
    content: string,
    size: number,
    color: string = PAGE_THEME.text,
    bold = false,
    originX = 0.5
  ): Phaser.GameObjects.Text {
    return this.add
      .text(x, y, content, {
        color,
        fontFamily: PAGE_THEME.font,
        fontSize: `${size}px`,
        fontStyle: bold ? 'bold' : 'normal',
      })
      .setOrigin(originX, 0.5);
  }

  /** 自己購買（主按鈕）。付款尚未開通，先提示。 */
  private renderBuyButton(x: number, y: number): void {
    const surface = createDialogButtonSurface(this, x, y, BUTTON_WIDTH, BUTTON_HEIGHT);
    surface.setPrimary();
    this.text(x, y, '購買', BUTTON_FONT, '#ffffff', true);
    const hit = this.add
      .zone(x, y, BUTTON_WIDTH, BUTTON_HEIGHT)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => this.buy());
    surface.bindInteraction(hit);
  }

  /** 請人代付（附加選項）：底線小字，不做成按鈕，避免比「購買」搶眼。 */
  private renderHelpLink(x: number, y: number, item: IShopItem): void {
    const label = this.text(x, y, '請人幫忙付 ›', HELP_LINK_FONT, HELP_LINK_COLOR);
    const underline = this.add
      .rectangle(x, y + label.height / 2, label.width, 1, HELP_LINK_LINE)
      .setOrigin(0.5, 0);
    this.add
      .zone(x, y, label.width + HELP_LINK_PAD * 2, label.height + HELP_LINK_PAD * 2)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => underline.setFillStyle(HELP_LINK_LINE_HOVER))
      .on('pointerout', () => underline.setFillStyle(HELP_LINK_LINE))
      .on('pointerup', () => this.askHelp(item));
  }

  private buy(): void {
    playUIClick(this);
    showNotice('線上付款即將開放，敬請期待');
  }

  /**
   * 產生代買連結並開 LINE 分享。手機瀏覽器會擋「等網路回來才開」的新視窗，
   * 所以點擊當下先開空白分頁，連結回來再導過去；被擋時改成本頁直接前往。
   */
  private askHelp(item: IShopItem): void {
    if (this.busy) return;
    this.busy = true;
    playUIClick(this);
    const tab = window.open('', '_blank');
    requestHelp(item.id, '商店')
      .then(({ shareUrl }) => {
        showNotice('LINE 打開後，選要傳給誰就好');
        if (tab) tab.location.href = shareUrl;
        else window.location.href = shareUrl;
      })
      .catch((error: unknown) => {
        tab?.close();
        noticeFromError(error);
      })
      .finally(() => {
        this.busy = false;
      });
  }

  private goBack(): void {
    playUIClick(this);
    this.scene.start(SceneKey.Home);
  }
}
